import React, { useState, useMemo, useEffect } from 'react';
import {Box, Select, TextInput, DateInput, Button} from 'grommet';
import BaseFilter from './BaseFilter';
import {formatNumericDate, getDateFromPast, parseNumericDate} from "../../utils/dates";

const dateOptions = [
    'in the last',
    'is equal to',
    'is between',
    'is on or after',
    'is before or on'
];

const pastOptions = ['days', 'weeks', 'months'];

// Helper to determine which option to select based on date range values
const getOptionFromRange = (start, end) => {
    if (!start && !end) return '';
    if (start && end && start === end) return 'is equal to';
    if (start && end) return 'is between';
    if (start && !end) return 'is on or after';
    if (!start && end) return 'is before or on';
    return '';
};

const DateFilter = ({ filterName = "Date", selectedRange, onApplyFilter}) => {
    const initialDateRange = useMemo(() => {
        return selectedRange
            ? { start: parseNumericDate(selectedRange[0]), end: parseNumericDate(selectedRange[1]) }
            : { start: '', end: '' };
    }, [selectedRange]);

    const [selectedOption, setSelectedOption] = useState(() =>
        getOptionFromRange(selectedRange?.[0], selectedRange?.[1])
    );
    const [dateRange, setDateRange] = useState(initialDateRange);
    const [lastValue, setLastValue] = useState('');
    const [lastUnit, setLastUnit] = useState('days');

    // Sync state when selectedRange prop changes (e.g., from URL on navigation back)
    useEffect(() => {
        // Only sync if we have actual values - check for truthy values (not '', undefined, null)
        const hasStart = selectedRange?.[0] !== undefined && selectedRange?.[0] !== '' && selectedRange?.[0] !== null;
        const hasEnd = selectedRange?.[1] !== undefined && selectedRange?.[1] !== '' && selectedRange?.[1] !== null;
        if (!hasStart && !hasEnd) return;

        const start = hasStart ? parseNumericDate(selectedRange[0]) : '';
        const end = hasEnd ? parseNumericDate(selectedRange[1]) : '';
        setDateRange({ start, end });
        setSelectedOption(getOptionFromRange(start, end));
    }, [selectedRange]);

    const renderContent = () => {
        const renderOptions = () => {
            switch (selectedOption) {
            case 'in the last':
                return (
                    <Box gap="xsmall">
                        <TextInput
                            size="small"
                            placeholder="1"
                            value={lastValue}
                            onChange={(e) => setLastValue(e.target.value)}
                        />
                        <Select
                            size="small"
                            options={pastOptions}
                            value={lastUnit}
                            onChange={({option}) => setLastUnit(option)}
                        />
                    </Box>
                )
            case 'is equal to':
                return (
                    <DateInput
                        size="small"
                        format="mm/dd/yyyy"
                        calendarProps={{daysOfWeek: true}}
                        value={dateRange.start}
                        onChange={({value}) => setDateRange({...dateRange, start: value, end: value})}
                    />
                )
            case 'is on or after':
                return (
                    <DateInput
                        size="small"
                        format="mm/dd/yyyy"
                        calendarProps={{daysOfWeek: true}}
                        value={dateRange.start}
                        onChange={({value}) => setDateRange({...dateRange, start: value, end: ''})}
                    />
                )
            case 'is before or on':
                return (
                    <DateInput
                        size="small"
                        format="mm/dd/yyyy"
                        calendarProps={{daysOfWeek: true}}
                        value={dateRange.end}
                        onChange={({value}) => setDateRange({...dateRange, start: '', end: value})}
                    />
                )
            case 'is between':
                return (
                    <Box gap="small">
                        <DateInput
                            size="small"
                            format="mm/dd/yyyy"
                            calendarProps={{daysOfWeek: true}}
                            value={dateRange.start}
                            onChange={({value}) => setDateRange({...dateRange, start: value})}
                        />
                        <DateInput
                            size="small"
                            format="mm/dd/yyyy"
                            calendarProps={{daysOfWeek: true}}
                            value={dateRange.end}
                            onChange={({value}) => setDateRange({...dateRange, end: value})}
                        />
                    </Box>
                )
            default:
                return null;
            }
        }

        return(
            <Box gap="xsmall" responsive={false}>
                <Select
                    size="small"
                    options={dateOptions}
                    value={selectedOption}
                    onChange={({ option }) => setSelectedOption(option)}
                    placeholder="Select filter type"
                />
                <Box gap="xsmall" responsive={false}>
                    {renderOptions()}
                    <Button fill type="submit" primary label="Apply"/>
                </Box>
            </Box>
        );
    }

    const renderButtonLabel = () => {
        if (selectedOption === '') { return null }
        const start = formatNumericDate(dateRange.start, '/')
        const end = formatNumericDate(dateRange.end, '/')

        if (selectedOption === dateOptions[0]) return `${lastValue} ${lastUnit}`;
        if (selectedOption === dateOptions[1]) return `${start}`;
        if (selectedOption === dateOptions[2]) return `${start} - ${end}`;
        if (selectedOption === dateOptions[3]) return `Starting from ${start}`;
        if (selectedOption === dateOptions[4]) return `Ending on ${end}`;
    }

    const onApply = () => {
        let startDate, endDate;

        if (selectedOption === dateOptions[0]) {
            startDate = formatNumericDate(getDateFromPast(lastValue, lastUnit), '-');
            endDate = formatNumericDate(new Date(), '-');
        } else {
            startDate = dateRange.start ? formatNumericDate(dateRange.start, '-') : '';
            endDate = dateRange.end ? formatNumericDate(dateRange.end, '-') : '';
        }

        if (startDate || endDate) {
            onApplyFilter({ start: startDate, end: endDate });
        } else {
            onReset();
        }
    }

    const onReset = () => {
        setSelectedOption('');
        setDateRange({ start: '', end: '' });
        setLastValue('');
        setLastUnit('days');
        onApplyFilter(undefined);
    }

    return (
        <BaseFilter
            filterName={filterName}
            renderContent={renderContent}
            renderButtonLabel={renderButtonLabel}
            isActive={selectedOption !== ''}
            onReset={onReset}
            onApply={onApply}
        />
    );
};

export default DateFilter;
